/* DFW tire pilot — progressive enhancement over the native Empire product form.
   Fail-closed contract:
   - body.aw-tire-live is added only after the selected variant has been resolved against the embedded JSON;
     until then the offer panel is hidden by CSS and the native price wrapper [data-aw-native-price] stays visible.
   - an unknown / invalid selection clears the panel (no price, no pickup promise, proxy CTA disabled);
     it never falls back to a previous variant.
   - the native quantity input is the only source of truth: its value is never coerced; an invalid value
     (empty, 0, decimal, non-numeric, outside native min/max) shows a prompt instead of a subtotal and disables the proxy CTA.
   Native submission is preserved unless the customer opts into local services;
   selected services are added with the tires through the Shopify Cart API. No variant changes. */
(() => {
  const QTY_PROMPT = 'Enter a quantity of 1 or more';

  const init = () => {
    const panel = document.querySelector('[data-aw-tire-offer]');
    if (!panel || panel.dataset.ready) return;
    panel.dataset.ready = 'true';

    const dataNode = document.querySelector('[data-aw-tire-variants]');
    let data;
    try { data = JSON.parse(dataNode.textContent); } catch (e) { return; }
    if (!data || !Array.isArray(data.variants) || !data.variants.length) return;

    const root = panel.closest('.product--outer') || document;
    const selection = root.querySelector('variant-selection');
    const form = root.querySelector('form[data-product-form]');
    const qty = form && form.querySelector('[name="quantity"]');
    const add = form && form.querySelector('button[data-product-atc]');
    const idInput = form && form.querySelector('[name="id"]');
    const nativePrice = root.querySelector('[data-aw-native-price]');

    /* Keep the native selector in its form; put help and pricing directly below it. */
    if (selection && form && selection.closest('form') === form) {
      const modelUse = panel.querySelector('.aw-tire-model-use');
      if (modelUse) panel.before(modelUse);
      selection.after(panel);
    }

    const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    const setText = (key, value) => root.querySelectorAll('[data-aw-' + key + ']').forEach(el => { el.textContent = value; });
    const stateOf = v => !v.available ? 'unavailable' : v.pilot ? 'ready' : 'contact';
    const tires = n => n + (n === 1 ? ' tire' : ' tires');

    /* Native quantity validity: positive integer that also passes the input's own constraints (min/max/step). */
    const readQty = () => {
      if (!qty) return 1;
      const raw = String(qty.value).trim();
      if (!/^\d+$/.test(raw)) return null;
      const n = parseInt(raw, 10);
      if (n < 1) return null;
      if (typeof qty.checkValidity === 'function' && !qty.checkValidity()) return null;
      return n;
    };

    const pickupTarget = el => el.querySelector('[data-aw-pickup-text]') || el;
    const renderPickup = (el, state) => {
      const t = pickupTarget(el);
      t.textContent = '';
      el.dataset.state = state;
      if (state === 'ready') {
        t.append("Local pickup is available by appointment only.");
      } else if (state === 'contact') {
        t.append("Local pickup is available by appointment only.");
      } else if (state === 'unavailable') {
        t.append('Not available in this size right now');
      } else {
        t.append('Choose a size to see price and pickup timing');
      }
    };

    let current = null;
    let subtotal = null;
    const listeners = [];
    const onChange = fn => listeners.push(fn);
    const notify = () => listeners.forEach(fn => fn());

    const currentId = () => {
      if (selection) return selection.getAttribute('variant');
      if (idInput && idInput.value) return idInput.value;
      return data.initial;
    };

    const servicesBox = root.querySelector('[data-aw-services]');
    const installCheck = servicesBox && servicesBox.querySelector('[data-aw-install]');
    const disposalCheck = servicesBox && servicesBox.querySelector('[data-aw-disposal]');
    const installQty = servicesBox && servicesBox.querySelector('[data-aw-install-quantity]');
    const disposalQty = servicesBox && servicesBox.querySelector('[data-aw-disposal-quantity]');
    const serviceError = servicesBox && servicesBox.querySelector('[data-aw-service-error]');
    let services = [];
    try { services = JSON.parse(root.querySelector('[data-aw-services-data]').textContent).variants; } catch (e) { /* Native tire purchase remains available. */ }
    const serviceBySku = sku => services.find(service => service.sku === sku && service.available);
    let installService = null;
    const disposalService = serviceBySku('DFW-DISPOSAL-1');
    let serviceVariantId = null;
    let submittingServices = false;
    let serviceTireQty = null;
    const servicesChosen = () => servicesBox && (installCheck.checked || disposalCheck.checked);
    const readServiceQty = input => {
      const n = readQty();
      const raw = String(input.value).trim();
      if (n === null || !/^\d+$/.test(raw)) return null;
      const count = Number(raw);
      return count >= 1 && count <= n && input.checkValidity() ? count : null;
    };
    const serviceQuantitiesValid = () => !servicesBox ||
      ((!installCheck.checked || readServiceQty(installQty) !== null) &&
       (!disposalCheck.checked || readServiceQty(disposalQty) !== null));
    const serviceTotal = () => !servicesBox ? 0 :
      (installCheck.checked && installService ? installService.price * (readServiceQty(installQty) || 0) : 0) +
      (disposalCheck.checked && disposalService ? disposalService.price * (readServiceQty(disposalQty) || 0) : 0);
    const syncServices = () => {
      if (!servicesBox) return;
      const n = readQty();
      const eligible = current && current.pilot && current.services && current.available && window.AWLocalTireServices;
      if (serviceVariantId !== (current && current.id)) {
        installCheck.checked = false; disposalCheck.checked = false;
        serviceVariantId = current && current.id;
        serviceTireQty = null;
      }
      if (n !== serviceTireQty) {
        installQty.value = disposalQty.value = n === null ? '' : String(n);
        serviceTireQty = n;
      }
      installQty.max = disposalQty.max = n === null ? '' : String(n);
      installService = eligible ? serviceBySku(window.AWLocalTireServices.installationSku(Number(current.diameter))) : null;
      installCheck.disabled = !installService || n === null;
      disposalCheck.disabled = !eligible || !disposalService || n === null;
      if (installCheck.disabled) installCheck.checked = false;
      if (disposalCheck.disabled) disposalCheck.checked = false;
      installQty.disabled = !installCheck.checked;
      disposalQty.disabled = !disposalCheck.checked;
      servicesBox.querySelector('[data-aw-install-quantity-row]').hidden = !installCheck.checked;
      servicesBox.querySelector('[data-aw-disposal-quantity-row]').hidden = !disposalCheck.checked;
      const installCount = installCheck.checked ? readServiceQty(installQty) : n;
      const disposalCount = disposalCheck.checked ? readServiceQty(disposalQty) : n;
      servicesBox.querySelector('[data-aw-install-price]').textContent = installService ? money(installService.price) + ' / tire' + (installCount ? ' · ' + money(installService.price * installCount) + ' for ' + tires(installCount) : '') : 'Contact us for a quote';
      servicesBox.querySelector('[data-aw-install-help]').textContent = installService && n !== null ? 'Wheel removal and refitting included.' : 'Choose a valid tire size and quantity to add installation.';
      servicesBox.querySelector('[data-aw-disposal-price]').textContent = disposalService ? money(disposalService.price) + ' / tire' + (disposalCount ? ' · ' + money(disposalService.price * disposalCount) + ' for ' + tires(disposalCount) : '') : 'Contact us for a quote';
      if (serviceQuantitiesValid()) serviceError.hidden = true;
      form.querySelectorAll('.shopify-payment-button').forEach(el => { el.hidden = !current || !current.available || !!servicesChosen(); });
      servicesBox.hidden = !current || !current.services;
      const caption = root.querySelector('[data-aw-caption]');
      if (caption) caption.textContent = 'Tire price only · Local pickup.' + (servicesBox.hidden ? '' : ' Optional installation and disposal priced separately.');
    };

    const updateSubtotal = () => {
      if (!subtotal) return;
      if (!current) { subtotal.textContent = ''; subtotal.classList.remove('is-invalid'); return; }
      const n = readQty();
      if (n === null) {
        subtotal.textContent = QTY_PROMPT;
        subtotal.classList.add('is-invalid');
        return;
      }
      if (!serviceQuantitiesValid()) {
        subtotal.textContent = 'Choose a valid quantity for each selected service';
        subtotal.classList.add('is-invalid');
        return;
      }
      subtotal.classList.remove('is-invalid');
      subtotal.textContent = 'Total: ' + money(current.price * n + serviceTotal()) + ' before tax';
      const breakdown = document.createElement('small');
      breakdown.textContent = tires(n) + ': ' + money(current.price * n) + (serviceTotal() ? ' · Services: ' + money(serviceTotal()) : '');
      subtotal.appendChild(breakdown);
    };

    const update = () => {
      const id = currentId();
      const v = data.variants.find(x => String(x.id) === String(id));
      if (!v) {
        /* unknown or incomplete selection: clear everything variant-specific */
        current = null;
        panel.dataset.state = 'none';
        setText('price', '');
        setText('four', '');
        setText('sku', '');
        root.querySelectorAll('[data-aw-pickup]').forEach(el => renderPickup(el, 'none'));
      } else {
        current = v;
        const state = stateOf(v);
        panel.dataset.state = state;
        setText('price', money(v.price));
        setText('four', money(v.price * 4));
        setText('sku', v.sku || '');
        root.querySelectorAll('[data-aw-pickup]').forEach(el => renderPickup(el, state));
      }
      if (form) form.dataset.awTireSelectionState = !v ? 'incomplete' : v.available ? 'ready' : 'unavailable';
      if (add && (!v || !v.available)) add.disabled = true;
      /* The Marketplace item code belongs to the selected size: rebuild the description line from variant data. */
      document.querySelectorAll('.product-description > p, .aw-tire-details > p').forEach(p => {
        if (!/^\s*Item code:/.test(p.textContent)) return;
        p.hidden = !v || !v.code;
        if (!v || !v.code) return;
        const strong = document.createElement('strong');
        strong.textContent = 'Item code: ' + v.code;
        p.replaceChildren(strong, ' · ' + v.title + ' only');
      });
      const guide = root.querySelector('[data-aw-code-guide]');
      if (guide && window.AWTireMarkings) {
        window.AWTireMarkings.render(guide, v ? v.title : null);
        root.classList.add('aw-tire-code-ready');
      }
      syncServices();
      updateSubtotal();
      notify();
    };

    /* Quantity shortcuts + live subtotal (native quantity input stays the source of truth). */
    if (qty && form) {
      const shortcuts = document.createElement('div');
      shortcuts.className = 'aw-tire-qty';
      shortcuts.setAttribute('role', 'group');
      shortcuts.setAttribute('aria-label', 'Choose tire quantity');
      let syncQuantity = () => {};
      [1, 2, 4].forEach(n => {
        const b = document.createElement('button');
        b.type = 'button';
        b.dataset.qty = String(n);
        b.textContent = tires(n);
        b.addEventListener('click', () => {
          qty.value = String(n);
          qty.dispatchEvent(new Event('input', { bubbles: true }));
          qty.dispatchEvent(new Event('change', { bubbles: true }));
          syncQuantity();
        });
        shortcuts.appendChild(b);
      });
      subtotal = document.createElement('p');
      subtotal.className = 'aw-tire-subtotal';
      subtotal.setAttribute('data-aw-subtotal', '');
      subtotal.setAttribute('aria-live', 'polite');

      const target = form.querySelector('.product-form__action-wrapper');
      if (target) { target.before(shortcuts); target.before(subtotal); }
      else { form.append(shortcuts, subtotal); }

      syncQuantity = () => {
        const n = readQty();
        shortcuts.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(n !== null && Number(b.dataset.qty) === n)));
        syncServices();
        updateSubtotal();
        notify();
      };
      qty.addEventListener('input', syncQuantity);
      qty.addEventListener('change', syncQuantity);
      syncQuantity();
    }

    if (form) form.addEventListener('submit', event => {
      const selected = data.variants.find(v => String(v.id) === String(currentId()));
      if (!selected || !selected.available || !idInput || String(idInput.value) !== String(selected.id)) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, true);

    if (servicesBox && form && add && window.AWLocalTireServices) {
      const target = form.querySelector('.product-form__action-wrapper');
      if (target) { target.before(servicesBox); target.before(subtotal); }
      else form.appendChild(servicesBox);
      // Service choices are not size changes: the native form resets quantity on other change events.
      const serviceChanged = event => { event.stopPropagation(); syncServices(); updateSubtotal(); notify(); };
      servicesBox.addEventListener('change', serviceChanged);
      servicesBox.addEventListener('input', serviceChanged);
      form.addEventListener('submit', async event => {
        if (!servicesChosen() && !submittingServices) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (submittingServices) return;
        syncServices();
        const n = readQty();
        if (!current || !current.available || !current.pilot || !current.services || n === null || !servicesChosen()) return;
        if (!serviceQuantitiesValid()) {
          serviceError.textContent = 'Choose a quantity from 1 to ' + n + ' for each selected service.';
          serviceError.hidden = false;
          return;
        }
        submittingServices = true;
        add.disabled = true; add.setAttribute('aria-busy', 'true');
        serviceError.hidden = true;
        const group = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2);
        const tire = { id: current.id, quantity: n, properties: { _aw_local_tire: 'true', _aw_local_group: group, _aw_diameter: String(current.diameter) } };
        const nativeData = new FormData(form);
        for (const [key, value] of nativeData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) tire.properties[match[1]] = value;
        }
        if (nativeData.get('selling_plan')) tire.selling_plan = nativeData.get('selling_plan');
        const items = [tire];
        const properties = { 'For tires': current.title, _aw_local_group: group };
        if (installCheck.checked) items.push({ id: installService.id, quantity: readServiceQty(installQty), parent_id: current.id, properties });
        if (disposalCheck.checked) items.push({ id: disposalService.id, quantity: readServiceQty(disposalQty), parent_id: current.id, properties });
        const route = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        try {
          const response = await fetch(route + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
          // Even an inventory error may partially add items. Never retry an ambiguous write.
          if (!response.ok) throw new Error('cart-add-failed');
          window.location.assign(route + 'cart');
        } catch (e) {
          serviceError.textContent = 'We could not confirm the complete addition. Review your cart before trying again. ';
          const link = document.createElement('a'); link.href = route + 'cart'; link.textContent = 'Review cart'; serviceError.appendChild(link);
          serviceError.hidden = false;
          // Remain locked until reload/cart review to avoid duplicate charges after a network error.
          add.removeAttribute('aria-busy');
        }
      }, true);
    }

    window.addEventListener('pageshow', event => {
      if (!event.persisted || !submittingServices) return;
      submittingServices = false;
      add.disabled = !current || !current.available; add.removeAttribute('aria-busy');
      if (serviceError) serviceError.hidden = true;
      update();
    });

    /* Pickup stays with the purchase; the long description moves below the hero. Native form stays intact. */
    const after = root.querySelector('[data-aw-after-form]');
    if (after && form) form.after(after);
    const description = root.querySelector('.product-block--description');
    const details = description && description.querySelector('.aw-tire-details');
    if (description && details) {
      const below = document.createElement('div');
      below.className = 'aw-tire-below';
      root.after(below);
      below.append(description);
      /* Model highlights sit with the photo on desktop and open the description on smaller screens. */
      const pick = s => details.querySelector(':scope > ' + s);
      const parts = ['h2', '.aw-tire-lead', '.aw-tire-benefits'].map(pick).filter(Boolean);
      const kicker = pick('.aw-tire-kicker');
      const benefits = pick('.aw-tire-benefits');
      if (benefits && [...details.querySelectorAll('.aw-tire-specs dt')].some(dt => /warranty/i.test(dt.textContent))) {
        benefits.querySelectorAll(':scope > li').forEach(li => { if (/warranty/i.test(li.textContent)) li.hidden = true; });
      }
      const gallery = [...root.children].find(el => el.classList.contains('product-gallery') && getComputedStyle(el).display !== 'none');
      if (parts.length && gallery && window.matchMedia) {
        const highlights = document.createElement('section');
        highlights.className = 'aw-tire-highlights';
        highlights.append(...parts);
        if (kicker) kicker.hidden = true; /* repeats the model line shown under the title */
        const media = document.createElement('div');
        media.className = 'aw-tire-media';
        const wide = window.matchMedia('(min-width: 1000px)');
        const COMPACT_HEADER = 82; /* the sticky header shrinks to 66px once scrolled, plus breathing room */
        const place = () => {
          if (wide.matches) {
            if (!media.parentNode) { gallery.before(media); media.append(gallery); }
            media.append(highlights);
            if (benefits) highlights.append(benefits);
            /* Pin as much of the column as fits under the compact header: everything, then photo + intro
               (benefit rows move to the description), then the photo alone. */
            const fits = () => media.offsetHeight + COMPACT_HEADER <= window.innerHeight;
            if (!fits() && benefits) details.prepend(benefits);
            if (!fits()) { details.prepend(highlights); if (benefits) highlights.append(benefits); }
            media.classList.toggle('is-static', gallery.offsetHeight + COMPACT_HEADER > window.innerHeight);
          } else {
            if (media.parentNode) { media.before(gallery); media.remove(); }
            if (benefits) highlights.append(benefits);
            details.prepend(highlights);
          }
          window.dispatchEvent(new Event('aw-tire-layout'));
        };
        let layoutTimer = 0;
        window.addEventListener('resize', () => { clearTimeout(layoutTimer); layoutTimer = setTimeout(place, 150); });
        window.addEventListener('load', place);
        setTimeout(place, 1500); /* gallery images settle after load */
        place();
        wide.addEventListener('change', place);
      }
    }

    /* Desktop sticky media column: follow the header's current height (it shrinks after scrolling). Layout is decided
       on load/resize only, so scrolling never switches pinning on or off. */
    let stickyFrame = 0;
    const setStickyTop = () => {
      stickyFrame = 0;
      if (window.innerWidth < 1000) return;
      let offset = 24;
      document.querySelectorAll('header, [class*="site-header"], [data-site-header]').forEach(el => {
        const pos = getComputedStyle(el).position;
        if (pos !== 'sticky' && pos !== 'fixed') return;
        const box = el.getBoundingClientRect();
        if (box.height > 0 && box.top <= 1 && box.bottom + 16 > offset) offset = Math.round(box.bottom + 16);
      });
      document.body.style.setProperty('--aw-sticky-top', offset + 'px');
    };
    const queueSticky = () => { if (!stickyFrame) stickyFrame = requestAnimationFrame(setStickyTop); };
    setStickyTop();
    window.addEventListener('resize', queueSticky);
    window.addEventListener('scroll', queueSticky, { passive: true });

    /* Mobile sticky bar: proxies the native Add to cart button; hidden while the real button is on screen.
       Shows the quantity and total that the native form will submit; disabled when the native quantity is invalid. */
    if (add) {
      const bar = document.createElement('div');
      bar.className = 'aw-tire-sticky';
      bar.hidden = true;
      const text = document.createElement('div');
      text.className = 'aw-tire-sticky__text';
      const total = document.createElement('span'); total.className = 'aw-tire-sticky__price';
      const detail = document.createElement('span'); detail.className = 'aw-tire-sticky__size';
      text.append(total, detail);
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Add to cart';
      button.addEventListener('click', () => { if (!button.disabled) add.click(); });
      bar.append(text, button);
      document.body.append(bar);

      const sync = () => {
        if (!current) {
          total.textContent = 'Select a size';
          detail.textContent = '';
          button.disabled = true;
          button.textContent = 'Add to cart';
          return;
        }
        const n = readQty();
        const unavailable = add.disabled || stateOf(current) === 'unavailable';
        if (n === null || !serviceQuantitiesValid()) {
          total.textContent = n === null ? QTY_PROMPT : 'Check service quantities';
          detail.textContent = current.title + (serviceTotal() ? ' · before tax' : ' · ' + money(current.price) + ' / tire');
        } else {
          total.textContent = money(current.price * n + serviceTotal()) + ' · ' + tires(n) + (serviceTotal() ? ' + services' : '');
          detail.textContent = current.title + (serviceTotal() ? ' · before tax' : ' · ' + money(current.price) + ' / tire');
        }
        button.disabled = unavailable || n === null || !serviceQuantitiesValid();
        button.textContent = add.disabled ? (add.textContent.trim() || 'Unavailable') : (n === null ? 'Add to cart' : 'Add ' + n + ' to cart');
      };
      onChange(sync);
      new MutationObserver(sync).observe(add, { attributes: true, attributeFilter: ['disabled'] });
      new MutationObserver(sync).observe(add, { childList: true, subtree: true, characterData: true });
      let addVisible = true;
      let selectorVisible = !!selection;
      const visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === add) addVisible = entry.isIntersecting;
          if (entry.target === selection) selectorVisible = entry.isIntersecting;
        });
        bar.hidden = addVisible || selectorVisible;
      });
      visibility.observe(add);
      if (selection) visibility.observe(selection);
    }

    /* First resolution, then go live: panel becomes visible, native price wrapper is hidden. */
    update();
    document.body.classList.add('aw-tire-live');
    if (nativePrice) { nativePrice.hidden = true; nativePrice.setAttribute('aria-hidden', 'true'); }

    if (selection) new MutationObserver(update).observe(selection, { attributes: true, attributeFilter: ['variant'] });
    if (form) form.addEventListener('change', () => setTimeout(update, 0));
    window.addEventListener('popstate', update);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
